/**
 * TravelChipBar — horizontal browse chips shown on /travel.
 * Powered by /api/travel/popular-chips. Three rows:
 *   1. Types (Airlines · Airports · Rail…)
 *   2. Popular by country (Airlines in United States…)
 *   3. Curated quick jumps (European Rail, NYC Terminals, …)
 * Every chip deep-links into TravelListPage with the right filter.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Plane, Building2, Train, Bus, Ship, Anchor, Car, MapPin, Sparkles, ArrowRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON = {
  plane: Plane, landmark: Building2, "train-track": Train,
  train: Train, bus: Bus, ship: Ship, anchor: Anchor, car: Car,
};

function Chip({ icon, label, url, count, variant = "default" }) {
  const Icon = ICON[icon] || MapPin;
  const bg = variant === "quick" ? "var(--wofy-primary)" : "var(--wofy-surface)";
  const color = variant === "quick" ? "#ffffff" : "inherit";
  const border = variant === "quick" ? "transparent" : "var(--wofy-border)";
  return (
    <Link
      to={url}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap border transition hover:shadow-md hover:-translate-y-0.5"
      style={{ background: bg, color, borderColor: border }}
      data-testid={`travel-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      {count != null && (
        <span
          className="text-[10px] px-1.5 rounded-full font-mono"
          style={{
            background: variant === "quick" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.06)",
            color: variant === "quick" ? "#ffffff" : "inherit",
          }}
        >{count}</span>
      )}
    </Link>
  );
}

function ChipRow({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-widest opacity-60 mb-2 font-mono">{label}</div>
      <div className="flex gap-2 flex-wrap">{children}</div>
    </div>
  );
}

export default function TravelChipBar() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/travel/popular-chips`)
      .then(r => setData(r.data))
      .catch(() => setData({ types: [], countries: [], quick: [] }));
  }, []);

  if (!data) {
    return (
      <div className="wofy-card !p-5 space-y-3 animate-pulse">
        <div className="h-3 w-32 rounded" style={{ background: "var(--wofy-surface)" }} />
        <div className="flex gap-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 w-24 rounded-full" style={{ background: "var(--wofy-surface)" }} />
          ))}
        </div>
      </div>
    );
  }

  const hasAny = (data.types?.length || 0) + (data.countries?.length || 0) + (data.quick?.length || 0) > 0;
  if (!hasAny) return null;

  return (
    <section className="wofy-card !p-5 space-y-4" data-testid="travel-chip-bar">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-xl m-0">Browse in one click</h2>
        <Link to="/travel/operators/new" className="ml-auto text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid="chip-add-cta">
          Add a listing <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {data.quick?.length > 0 && (
        <ChipRow label="Quick jumps" testid="chip-row-quick">
          {data.quick.map(c => (
            <Chip key={c.url} {...c} variant="quick" />
          ))}
        </ChipRow>
      )}

      {data.countries?.length > 0 && (
        <ChipRow label="By country" testid="chip-row-countries">
          {data.countries.map(c => (
            <Chip key={c.url} {...c} />
          ))}
        </ChipRow>
      )}

      {data.types?.length > 0 && (
        <ChipRow label="By type" testid="chip-row-types">
          {data.types.map(c => (
            <Chip key={c.url} {...c} />
          ))}
        </ChipRow>
      )}
    </section>
  );
}
